import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../Spinner.jsx";
import Controls from "./Controls/Controls.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "./components/ContentDialog.jsx";
import { isDisabled, isHidden } from "./evaluations.js";
import { getRWObjectCopy } from "./helper.js";
import {
  compareObjects,
  evaluate,
  flattenItems,
  getEmptyModel,
  objIsNotEmpty,
} from "./helper.js";
import { processTransformations } from "./transformations.js";
import { dimensions } from "../../theme/index.js";
import DSButton from "../../theme/components/Button.jsx";
import { ClaimCostsEditor } from "../claim/costs/index.js";
import SchadenpositionenTable from "../claim/positions/SchadenpositionenTable.jsx";
import StehlgutHinweise from "../claim/positions/StehlgutHinweise.jsx";
import ClaimMasterDataCard from "../claim/wizard/ClaimMasterDataCard.jsx";
import ClaimContractCard from "../claim/wizard/ClaimContractCard.jsx";
import ClaimVehicleCard from "../claim/wizard/ClaimVehicleCard.jsx";
import ClaimRecap from "../claim/wizard/ClaimRecap.jsx";
import ClaimEinordnung from "../claim/wizard/ClaimEinordnung.jsx";
import ClaimLocation from "../claim/wizard/ClaimLocation.jsx";
import ClaimConfirm from "../claim/wizard/ClaimConfirm.jsx";

FormRenderer.propTypes = {
  fdef: PropTypes.object,
  model: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  editMode: PropTypes.bool,
  showToggleEdit: PropTypes.bool,
  showErrorText: PropTypes.bool,
  variant: PropTypes.string,
  showActions: PropTypes.bool,
  showCancel: PropTypes.bool,
  showDelete: PropTypes.bool,
  actionsPosition: PropTypes.string,
  cancelAction: PropTypes.func,
  cancelLabel: PropTypes.string,
  submitAction: PropTypes.object,
  submitAction2: PropTypes.object,
  deleteAction: PropTypes.object,
  toggleEditPosition: PropTypes.string,
  setActions: PropTypes.func,
  apiGet: PropTypes.func,
  useV2Layout: PropTypes.bool,
  stepperVariant: PropTypes.string,
  stepperProgressMode: PropTypes.string,
  delegateActions: PropTypes.bool,
  onDirtyChange: PropTypes.func,
};

/**
 * The FormRenderer is a component that renders a form based on a given
 * form definition. The form definition is a JSON object that contains
 * information about the form fields and how they should be rendered.
 *
 * @param {Object} props
 * @param {Object} props.fdef The form definition
 * @param {Object} props.model The data model
 * @param {Boolean} props.editMode Whether the form should be editable
 * @param {Boolean} props.showErrorText Whether error messages should be shown
 * @param {String} props.variant The variant of the form (e.g. "outlined")
 * @param {Boolean} props.showActions Whether the action buttons should be shown
 * @param {String} props.actionsPosition The position of the action buttons
 * @param {Function} props.cancelAction The callback for the cancel button
 * @param {String} props.cancelLabel The label for the cancel button
 * @param {Object} props.submitAction The callback for the submit button
 * @param {Object} props.deleteAction The callback for the delete button
 * @param {String} props.toggleEditPosition The position of the toggle edit button
 * @param {Function} props.setActions The callback for setting the action buttons
 * @param {Function} props.apiGet Callback for issuing an API GET request
 * @returns {ReactElement} The rendered form
 */
export default function FormRenderer(props) {
  const {
    fdef,
    model,
    editMode,
    showErrorText,
    variant,
    showActions = false,
    actionsPosition = "top",
    cancelAction = null,
    cancelLabel = "Abbruch",
    setActions = null,
    apiGet,
    useV2Layout = false,
    stepperVariant = null,
    stepperProgressMode = "lax",
    claimStatus = null,
    currentUserIsBroker = false,
    handoffCallback = null,
    brokerSubmitCallback = null,
    reclassifyCallback = null,
    delegateActions = false,
    showCancel = true,
    showDelete = false,
    submitAction,
    submitAction2,
    showToggleEdit,
    toggleEditPosition = "bottom",
    onDirtyChange = null,
  } = props;
  const navigate = useNavigate();
  const [isInitialized, setIsInitialized] = useState(false);
  const formDefinition = fdef.formdefinition;
  const [intModel, setIntModel] = useState({});
  const [originalValues, setOriginalValues] = useState({});
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [intVariant] = useState(variant || "outlined");
  const { enqueueSnackbar } = useSnackbar();
  const [intEditMode, setIntEditMode] = useState(editMode);
  const interpreterItems = flattenItems(fdef.formdefinition);
  const { openCD, setOpenCD, tca, setTca } = ContentDialogInit();
  const [canSave, setCanSave] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubFormOpen, setIsSubFormOpen] = useState(false);
  const debounceTimeout = useRef(null);
  const prevIsDirtyRef = useRef(false);
  const theme = useTheme();

  const validateForm = () => {
    const resultValidation = evaluate(
      "isSubFormValid()",
      values,
      interpreterItems
    );
    setIsValid(resultValidation.result);
    setCanSave(resultValidation.result && intEditMode && formHasChanged());
    setErrors(resultValidation.aerrors);
  };

  useEffect(() => {
    setIntEditMode(intEditMode);
    handleActions();
  }, [intEditMode]);

  useEffect(() => {
    if (objIsNotEmpty(values)) {
      validateForm();
      //processConnectors();
      handleActions();
    }
  }, [values, canSave]);

  // useEffect to rerender actions when isSubmitting changes
  // wird benötigt, damit die Buttons den loading state anzeigen können
  useEffect(() => {
    handleActions();
  }, [isSubmitting]);

  // notify parent about dirty state changes
  useEffect(() => {
    if (onDirtyChange && objIsNotEmpty(originalValues)) {
      const isDirty = formHasChanged();
      if (isDirty !== prevIsDirtyRef.current) {
        prevIsDirtyRef.current = isDirty;
        onDirtyChange(isDirty);
      }
    }
  }, [values, originalValues]);

  useEffect(() => {
    setValues(transformIn());
    setOriginalValues(transformIn());
    if (objIsNotEmpty(intModel)) {
      setIsInitialized(true);
    }
  }, [intModel]);

  useEffect(() => {
    if (formDefinition && model) {
      let modelToSet = getEmptyModel(formDefinition);
      for (const key in model) {
        const modelValue = model[key];
        modelToSet[key] = modelValue;
      }
      setIntModel(modelToSet);
    }
    // only reset form when loading a different entity, not on ref changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model?.id]);

  //*** Handler

  const handleActions = () => {
    if (setActions)
      setActions({ bottom: ActionButtons(), top: <ActionButtonsHeader /> });
  };

  const handleRemoveValues = (valuesToRemove) => {
    let newValues = { ...values };
    valuesToRemove.forEach((keyToRemove) => {
      if (newValues.hasOwnProperty(keyToRemove)) {
        delete newValues[keyToRemove];
      }
    });
    setValues(newValues);
  };

  const handleOnChange = (e) => {
    if (Array.isArray(e)) {
      e.forEach((item) => {
        setValues((prevValues) => {
          return { ...prevValues, [item.target.name]: item.target.value };
        });
      });
    } else {
      setValues((prevValues) => {
        return { ...prevValues, [e.target.name]: e.target.value };
      });
    }
  };

  const handleOnClick = async (e, action) => {
    e.preventDefault();
    const toSend = transformOut(values);
    if (action.additionalData) {
      action.additionalData.forEach((item) => {
        toSend[item.key] = item.value;
      });
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    switch (action.method) {
      case "callback":
        // check whether callback is async
        if (action.callback.toString().includes("async")) {
          await action.callback(toSend);
        } else {
          action.callback(toSend);
        }
        break;
      case "post":
        switch (action.type) {
          case "url":
            axios
              .post(action.target, toSend, { headers: action.headers || {} })
              .then((res) => {
                if (res.status === 200) {
                  action.callback(res.data);
                  if (action.redirect) navigate(action.redirect);
                  setIntEditMode(false);
                } else {
                  showResponse(null, res, "error");
                }
              })
              .catch((e) => {
                showResponse(e, null, "error");
              })
              .finally(() => {
                setIsSubmitting(false);
              });
            break;
          default:
        }
        break;
      case "get":
        switch (action.type) {
          case "url":
            axios
              .get(action.target, { headers: action.headers || {} })
              .then((res) => {
                if (res.status === 200) {
                  action.callback(res.data);
                  navigate(action.redirect);
                  setIntEditMode(false);
                } else {
                  showResponse(null, res, "error");
                }
              })
              .catch((e) => {
                showResponse(e, null, "error");
              })
              .finally(() => {
                setIsSubmitting(false);
              });
            break;
          default:
        }
        break;
      case "delete":
        switch (action.type) {
          case "url":
            axios
              .delete(action.target, { headers: action.headers || {} })
              .then((res) => {
                if (res.status === 200) {
                  action.callback(res.data);
                  navigate(action.redirect);
                  setIntEditMode(false);
                } else {
                  showResponse(null, res, "error");
                }
              })
              .catch((e) => {
                showResponse(e, null, "error");
              })
              .finally(() => {
                setIsSubmitting(false);
              });
            break;
          default:
        }
        break;
      default:
        break;
    }

    setIsSubmitting(false);
  };

  const handleCancel = () => {
    if (cancelAction) cancelAction();
    else {
      resetForm();
      setIntEditMode(false);
    }
  };

  const handleToggleEditMode = () => {
    if (intEditMode) {
      resetForm();
      setIntEditMode(false);
    }
    setIntEditMode(!intEditMode);
  };

  //**** Form

  const formHasChanged = () => {
    return !compareObjects(originalValues, values);
  };

  const resetForm = () => {
    setValues(transformIn());
    setErrors({});
  };

  const showResponse = (e, response, variant) => {
    const message =
      response?.data?.message ||
      e?.response?.data?.message ||
      "Error - please contact Administrator";
    enqueueSnackbar(message, { variant: variant });
    if (e) console.error(e);
  };

  const getVisibleModel = (transformations) => {
    const extendedModel = createDataModels();
    const transformedModel = processTransformations(
      transformations,
      extendedModel,
      interpreterItems
    );
    /* let visibleModel = {};
    for (const key in transformedModel) {
      const formItems = formDefinition.filter((formItem) => formItem.name === key);
      let isVisible = true;
      if (formItems.length > 0) {
        let numVisible = 0;
        formItems.forEach((formItem) => {
          if (formItem.hidden) {
            if (!isHidden(formItem, values, interpreterItems).result) numVisible++;
          } else numVisible++;
        });
        isVisible = numVisible !== 0;
      }

      if (isVisible) visibleModel[key] = transformedModel[key];
    }
    console.log("visibleModel", visibleModel);
    return visibleModel;*/
    return transformedModel;
  };

  const createDataModels = () => {
    const extendedModel = getRWObjectCopy(values);
    formDefinition.forEach((item) => {
      if (item.control === "expandable") {
        const template = extendedModel[item.name + "#temp"];
        const toWrite = [];
        if (template) {
          template.forEach((row) => {
            const rowToWrite = {};
            row.forEach((column) => {
              const targetColumnName = column.split("#")[1];
              rowToWrite[targetColumnName] = extendedModel[column];
            });
            toWrite.push(rowToWrite);
          });
        }
        extendedModel[item.name] = toWrite;
      }
    });
    return extendedModel;
  };

  const transformOut = () => {
    return getVisibleModel(fdef.transformOut);
  };

  const transformIn = () => {
    return processTransformations(fdef.transformIn, intModel, interpreterItems);
  };

  const getValues = () => {
    return transformOut(values);
  };

  const evaluateExpression = (expression) => {
    return evaluate(expression, values, interpreterItems);
  };

  const getControl = (item, forceDisabled = false) => {
    const resIsDisabled = isDisabled(item, values, interpreterItems);
    const error = errors[item?.name];
    const value = values[item?.name];

    const controlParameter = {
      // forceDisabled lets a caller (e.g. PhaseRail) render a slice of fields
      // read-only — used to show the broker the customer's frozen fields.
      disabled: forceDisabled || !(intEditMode && !resIsDisabled.result),
      variant: intVariant,
      apiGet: apiGet,
      item: item,
      value: value,
      onChange: handleOnChange,
      error: error,
      errors: errors,
      showErrorText: showErrorText,
      onClick: handleOnClick,
      values: values,
      isSubFormOpen,
      // Pass action context for controls that need to render actions (e.g. StepperV2)
      actionContext: {
        showCancel,
        showDelete,
        submitAction,
        submitAction2,
        cancelLabel,
        handleOnClick,
        handleCancel,
        showYesNoDelete,
        canSave,
        isValid,
        intEditMode,
        handleToggleEditMode,
        showToggleEdit,
        toggleEditPosition,
        // only delegate actions when the form itself is configured to show actions
        delegateActions: delegateActions && showActions,
        // two-phase (PhaseRail) submit callbacks
        handoffCallback,
        brokerSubmitCallback,
        reclassifyCallback,
      },
    };

    switch (item.control) {
      case "sectiontitle":
        return (
          <Typography variant="subtitle1" color="primary">
            {item.label}
          </Typography>
        );
      case "buttongroup":
        return <Controls.ButtonGroup {...controlParameter} />;
      case "radiogroup":
        return <Controls.RadioGroup {...controlParameter} />;
      case "autocomplete":
        return (
          <Controls.AutoComplete
            {...controlParameter}
            dependencyValue={item.depends_on ? values[item.depends_on] : null}
          />
        );
      case "emailinput":
        return <Controls.EMailInput {...controlParameter} />;
      case "selectlist":
        return <Controls.SelectList {...controlParameter} />;
      case "datepicker":
        return <Controls.DatePicker {...controlParameter} />;
      case "timepicker":
        return <Controls.TimePicker {...controlParameter} />;
      case "input":
        return (
          <Controls.Input
            {...controlParameter}
            evaluateExpression={evaluateExpression}
          />
        );
      case "numberinput":
        return <Controls.NumberInput {...controlParameter} />;
      case "sum":
        return (
          <Controls.SumInput
            {...controlParameter}
            fields={item.fields}
            setValues={setValues}
          />
        );
      case "sliderinput":
        return <Controls.SliderInput {...controlParameter} />;
      case "twolabelslider":
        return <Controls.TwoLabelSlider {...controlParameter} />;
      case "rating":
        return <Controls.Rating {...controlParameter} />;
      case "password":
        return (
          <Controls.Password
            {...controlParameter}
            autoComplete={item.autoComplete}
          />
        );
      case "link":
        return <Controls.Link {...controlParameter} />;
      case "info":
        return <Controls.Info {...controlParameter} />;
      case "checkbox":
        return <Controls.Checkbox {...controlParameter} />;
      case "switch":
        return <Controls.Switch {...controlParameter} />;
      case "segmented":
        return <Controls.SegmentedInput {...controlParameter} />;
      case "chiptoggle":
        return <Controls.ChipToggle {...controlParameter} />;
      case "grouplabel":
        // A standalone field-group label (the click dummy's `labeled()` header),
        // e.g. "Liegen Nachweise vor?" above a row of chip toggles.
        return (
          <Typography
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: "#374151",
              mb: "-8px",
            }}
          >
            {item.label}
          </Typography>
        );
      case "button":
        return <Controls.Button {...controlParameter} />;
      case "fillbutton":
        return <Controls.FillButton {...controlParameter} />;
      case "select":
        return <Controls.Select {...controlParameter} />;
      case "upload":
        return <Controls.Upload {...controlParameter} />;
      case "textarea":
        return <Controls.Textarea {...controlParameter} />;
      case "detailed_radio_group":
        return <Controls.DetailedRadioGroup {...controlParameter} />;
      case "accordion":
        return (
          <Controls.Accordion
            {...controlParameter}
            renderFields={renderFields}
          />
        );
      case "expandable":
        return (
          <Controls.Expandable
            {...controlParameter}
            renderFields={renderFields}
            handleRemoveValues={handleRemoveValues}
          />
        );
      case "stepper":
        // `stepperVariant="journey"` swaps the whole stepper presentation for the
        // click-dummy journey shell (top stepper + footer nav). The form def shape
        // is unchanged, so validation/model/submit are untouched.
        const StepperComponent =
          stepperVariant === "journey"
            ? Controls.JourneyWizard
            : stepperVariant === "phaserail"
              ? Controls.PhaseRail
              : useV2Layout
                ? Controls.StepperV2
                : Controls.Stepper;
        return (
          <StepperComponent
            {...controlParameter}
            renderFields={renderFields}
            progressMode={stepperProgressMode}
            claimStatus={claimStatus}
            isBroker={currentUserIsBroker}
          />
        );
      case "sbwarning":
        return (
          <Controls.SBWarning
            {...controlParameter}
            sparteObj={model.sparteObj}
            values={values}
          />
        );
      case "blank":
        return <></>;
      case "schadenpositionen":
        return (
          <SchadenpositionenTable
            disabled={!intEditMode}
            value={value || []}
            onChange={(positions) =>
              handleOnChange({ target: { name: item.name, value: positions } })
            }
            onApplyTotal={
              item.applyTotalTo
                ? (t) =>
                    handleOnChange({
                      target: { name: item.applyTotalTo, value: t },
                    })
                : undefined
            }
          />
        );
      case "stehlgut_hinweise":
        return <StehlgutHinweise item={item} />;
      case "claim_masterdata_card":
        return <ClaimMasterDataCard model={model} values={values} />;
      case "claim_contract_card":
        return (
          <ClaimContractCard
            model={model}
            values={values}
            onChange={handleOnChange}
            disabled={controlParameter.disabled}
          />
        );
      case "claim_vehicle_card":
        return (
          <ClaimVehicleCard
            model={model}
            values={values}
            onChange={handleOnChange}
            disabled={controlParameter.disabled}
          />
        );
      case "claim_location":
        return (
          <ClaimLocation
            model={model}
            values={values}
            onChange={handleOnChange}
            disabled={controlParameter.disabled}
          />
        );
      case "claim_confirm":
        return (
          <ClaimConfirm
            values={values}
            onChange={handleOnChange}
            disabled={controlParameter.disabled}
          />
        );
      case "claim_recap":
        return <ClaimRecap item={item} values={values} />;
      case "claim_einordnung":
        return (
          <ClaimEinordnung
            currentSparte={item.currentSparte || model?.sparte || values?.sparte || ""}
            values={values}
            onChange={handleOnChange}
            disabled={controlParameter.disabled}
            onReclassify={(newSparte, e) =>
              handleOnClick(e, {
                method: "callback",
                callback: reclassifyCallback,
                additionalData: [{ key: "sparte", value: newSparte }],
              })
            }
          />
        );
      case "claim_costs_editor":
        return (
          <ClaimCostsEditor
            disabled={!intEditMode}
            value={value || []}
            onChange={(costs) =>
              handleOnChange({ target: { name: item.name, value: costs } })
            }
            onFormChange={handleOnChange}
            onFormOpenChange={setIsSubFormOpen}
          />
        );
      default:
        return "Unknown control: " + item.control;
    }
  };

  const normalizeCols = (cols, default_cols) => {
    const breakpoints = theme.breakpoints.keys;
    const normalized_cols = {};
    let prev = default_cols;
    breakpoints.forEach((bp) => {
      if (cols[bp]) {
        normalized_cols[bp] = cols[bp];
        prev = cols[bp];
      } else {
        normalized_cols[bp] = prev;
      }
    });

    return normalized_cols;
  };

  const renderFields = (fieldArray, forceDisabled = false) => {
    return fieldArray.map((item, index) => {
      const resIsHidden = isHidden(item, values, interpreterItems);
      // if item.cols is a number, use it for all larger breakpoints
      // if item.cols is an object, use its per-breakpoint values where defined
      // and automatically apply to greater breakpoints
      if (!resIsHidden.result) {
        let grid_props = {};
        const default_cols = 12;
        if (item && typeof item.cols === "object" && item.cols !== null) {
          grid_props = normalizeCols(item.cols, default_cols);
        } else {
          const num_columns = Number(item?.cols) || default_cols;
          grid_props = normalizeCols({ md: num_columns }, default_cols);
        }

        return (
          <Grid
            size={grid_props}
            key={index}
            sx={{
              // Steppers (journey / phaserail wizards) render their own top
              // chrome, so they must not inherit the per-control top padding.
              paddingTop: item.control === "stepper" ? 0 : dimensions["2xl"],
              width: "100%",
            }}
          >
            {getControl(item, forceDisabled)}
          </Grid>
        );
      }
    });
  };

  const showYesNoDelete = () => {
    const { deleteAction } = props;
    setOpenCD(true);
    setTca({
      title: "Löschen?",
      content: (
        <>
          <Typography>Soll der Datensatz wirklich gelöscht werden?</Typography>
          <Typography>
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </>
      ),
      maxWidth: "sm",
      actions: (
        <>
          <DSButton
            variant="tertiary"
            size="medium"
            sx={{ ml: "10px" }}
            onClick={() => setOpenCD(false)}
          >
            {cancelLabel}
          </DSButton>
          <Button
            sx={{ ml: "10px" }}
            color="delete"
            onClick={(e) => handleOnClick(e, deleteAction)}
          >
            Löschen
          </Button>
        </>
      ),
    });
  };

  const ActionButtons = () => {
    const {
      showCancel,
      showDelete,
      submitAction,
      submitAction2,
      toggleEditPosition,
      showToggleEdit,
    } = props;

    let disableSave = !canSave || isSubFormOpen;
    if (submitAction.alwaysActive && !isSubFormOpen) disableSave = false;

    // Determine if action2 button should be active
    // if the activeWhen function is defined, it will be called with the current form validity
    // we might extend the arguments in the future
    // if it is not defined, the button will be active if the canSave flag is true
    const isAction2Active =
      !isSubFormOpen &&
      (submitAction2?.activeWhen &&
      typeof submitAction2.activeWhen === "function"
        ? submitAction2.activeWhen(isValid)
        : canSave);

    return (
      <>
        {showDelete && (
          <Button
            sx={{ ml: "10px", textTransform: "none" }}
            color="delete"
            disabled={isSubFormOpen}
            onClick={() => showYesNoDelete()}
          >
            Löschen
          </Button>
        )}
        {showCancel && (
          <DSButton
            variant="tertiary"
            size="medium"
            sx={{ ml: "10px" }}
            disabled={isSubFormOpen}
            onClick={() => handleCancel()}
          >
            {cancelLabel}
          </DSButton>
        )}
        {submitAction2 && (
          <Button
            sx={{ ml: "10px", textTransform: "none" }}
            disabled={!isAction2Active}
            color={submitAction2.color || "primary"}
            variant={submitAction2.variant || "contained"}
            onClick={(e) => handleOnClick(e, submitAction2)}
            loading={submitAction2.isLoading || isSubmitting}
          >
            {submitAction2.label}
          </Button>
        )}
        {submitAction && (
          <Button
            sx={{ ml: "10px", textTransform: "none" }}
            disabled={disableSave}
            color={submitAction.color || "primary"}
            variant={submitAction.variant || "contained"}
            onClick={(e) => handleOnClick(e, submitAction)}
            loading={submitAction.isLoading || isSubmitting}
          >
            {submitAction.label || "Speichern"}
          </Button>
        )}
        {showToggleEdit && toggleEditPosition === actionsPosition && (
          <FormControlLabel
            sx={{ ml: "10px", gap: "1rem" }}
            control={
              <Switch
                checked={intEditMode}
                onChange={() => handleToggleEditMode()}
              />
            }
            label="Bearbeiten"
          />
        )}
      </>
    );
  };

  const ActionButtonsHeader = () => {
    const { toggleEditPosition, showToggleEdit } = props;
    return (
      <>
        {showToggleEdit && toggleEditPosition === "top" && (
          <FormControlLabel
            sx={{ ml: "10px", gap: "1rem" }}
            control={
              <Switch
                checked={intEditMode}
                onChange={() => handleToggleEditMode()}
              />
            }
            label="Bearbeiten"
          />
        )}
      </>
    );
  };

  const ActionButtonBar = () => {
    return (
      <Box
        display="flex"
        justifyContent="flex-end"
        sx={{ margin: "5px", pb: "10px", pt: "10px", height: "60px" }}
      >
        <ActionButtons />
      </Box>
    );
  };

  if (!isInitialized) {
    return <Spinner />;
  } else {
    return (
      <Fragment>
        {(actionsPosition === "top" || actionsPosition === "both") &&
          showActions &&
          !delegateActions && <ActionButtonBar />}
        <Grid container spacing={2}>
          {objIsNotEmpty(values) && renderFields(formDefinition)}
        </Grid>
        {(actionsPosition === "bottom" || actionsPosition === "both") &&
          showActions &&
          !delegateActions && <ActionButtonBar />}
        <ContentDialog openCD={openCD} setOpenCD={setOpenCD} tca={tca} />
      </Fragment>
    );
  }
}
